import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Icon } from './Icon'
import type { IconName } from './Icon'
import { useQuickReference } from '../hooks/useQuickReference'
import {
  useResourceOverrides,
  type EditableCategorySlug,
  type SafetyBriefingOverride,
  type EventPlanOverride,
  type FirstAidOverride,
  type Scope,
  type SafetyBriefingContent,
  type EventPlanContent,
  type FirstAidContent
} from '../hooks/useResourceOverrides'
import { EVENT_RESOURCE_CATEGORY_LABEL } from '../types'
import type { EventResourceCategory } from '../types'
import { SafetyBriefingTab } from './live/quickref/SafetyBriefingTab'
import { EventPlanTab } from './live/quickref/EventPlanTab'
import { ActivitiesTab } from './live/quickref/ActivitiesTab'
import { StationMapsTab } from './live/quickref/StationMapsTab'
import { KeyContactsTab } from './live/quickref/KeyContactsTab'
import { FirstAidTab } from './live/quickref/FirstAidTab'

// ResourceEditModal — copy-tuning playground for the Quick Reference
// panel's text-bearing tabs. Pure frontend; overrides stored in
// localStorage via useResourceOverrides.
//
// Two modes (per the work plan):
//   - defaults — tunes the org-wide constants in resourcesData.ts. All
//     6 tabs visible. Opened from /resources's top "Resources Edit
//     Defaults" row.
//   - event — overrides for a specific event. Tab strip hidden; only
//     the scoped category renders. Opened from any cell in an event row.
//
// Tab category split (work plan §"Per-tab modal behaviour"):
//   - Editable (form + live preview): briefing, plan, first-aid
//   - Read-only with link-out:        activities, maps, contacts
//
// The modal does NOT have a Save button. Form state is committed to the
// override store on each change; the preview pane updates live. The
// Reset button clears the active tab's override.

interface Props {
  mode: 'defaults' | 'event'
  eventId?: number
  eventName?: string
  initialCategory?: EventResourceCategory
  onClose: () => void
}

const TABS: ReadonlyArray<{ key: EventResourceCategory; label: string; icon: IconName }> = [
  { key: 'briefing',   label: 'Safety Briefing', icon: 'shield-check' },
  { key: 'plan',       label: 'Event Plan',      icon: 'file' },
  { key: 'activities', label: 'Activities',      icon: 'star' },
  { key: 'maps',       label: 'Station Maps',    icon: 'map' },
  { key: 'contacts',   label: 'Key Contacts',    icon: 'users' },
  { key: 'first-aid',  label: 'First Aid',       icon: 'first-aid' }
]

const EDITABLE: ReadonlyArray<EditableCategorySlug> = ['briefing', 'plan', 'first-aid']

function isEditable(c: EventResourceCategory): c is EditableCategorySlug {
  return (EDITABLE as readonly string[]).includes(c)
}

export function ResourceEditModal({
  mode,
  eventId,
  eventName,
  initialCategory,
  onClose
}: Props) {
  const [tab, setTab] = useState<EventResourceCategory>(initialCategory ?? 'briefing')
  const {
    clearOverride,
    hasOverride,
    resolveSafetyBriefing,
    resolveEventPlan,
    resolveFirstAid
  } = useResourceOverrides()

  // Per-event mode needs the DB value for the Event Plan resolver and
  // the live activities / location / contacts for the read-only tabs.
  // Defaults mode passes eventId=undefined to the hook (which is then
  // disabled internally) and renders read-only tabs with placeholder
  // copy explaining they aren't tunable.
  const { data: quickRef } = useQuickReference(
    mode === 'event' && eventId != null ? eventId : -1
  )

  const scope: Scope = mode === 'defaults' ? 'defaults' : { eventId: eventId! }
  const targetEventId = mode === 'event' ? (eventId ?? null) : null

  // Hooks unconditional (§2) — escape / scroll lock before any branch.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  useEffect(() => {
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = previous }
  }, [])

  const title = mode === 'defaults'
    ? 'Edit Defaults — Resources Panel'
    : `Edit ${EVENT_RESOURCE_CATEGORY_LABEL[tab]} — ${eventName ?? 'Event'}`

  // Tab strip is hidden in per-event mode (only the scoped category
  // renders), so we don't need to filter TABS — we just don't render
  // the strip.
  const showTabStrip = mode === 'defaults'

  const tabsToRender = mode === 'defaults' ? TABS : TABS.filter((t) => t.key === tab)

  // The currently-resolved content for the active tab. Drives both the
  // form's initial values and the preview render.
  const briefing = resolveSafetyBriefing(targetEventId)
  const plan = resolveEventPlan(
    targetEventId,
    quickRef
      ? {
          operatingPattern: quickRef.operatingPattern,
          todaysProgramme: quickRef.todaysProgramme,
          visitorFlow: quickRef.visitorFlow
        }
      : null
  )
  const firstAid = resolveFirstAid(targetEventId)

  function onReset() {
    if (!isEditable(tab)) return
    clearOverride(scope, tab)
  }

  const canReset = isEditable(tab) && hasOverride(scope, tab)

  return (
    <div
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="fixed inset-0 z-[400] flex items-center justify-center"
      style={{ background: 'rgba(0,0,0,0.7)', padding: 20 }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="text-ink rounded-xl shadow-2xl w-full flex flex-col overflow-hidden"
        style={{ background: '#fdfbf6', maxWidth: 920, height: '85vh' }}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-6 py-4 border-b border-line flex-shrink-0">
          <div className="min-w-0">
            <div className="font-display text-xl font-semibold leading-tight truncate">
              {title}
            </div>
            <div className="text-xs text-ink-soft italic mt-0.5">
              Preview only — changes are stored in your browser. Reset clears them.
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-ink-soft hover:text-ink hover:bg-line/40 rounded p-1.5 transition-colors flex-shrink-0"
          >
            <Icon name="x" size={18} />
          </button>
        </div>

        {/* Tab strip (defaults mode only) */}
        {showTabStrip && (
          <div
            className="flex gap-2 px-6 py-3 border-b border-line overflow-x-auto flex-shrink-0"
            role="tablist"
            aria-label="Edit defaults sections"
          >
            {TABS.map((t) => {
              const active = tab === t.key
              const tabHasOverride = isEditable(t.key) && hasOverride(scope, t.key)
              return (
                <button
                  key={t.key}
                  role="tab"
                  aria-selected={active}
                  type="button"
                  onClick={() => setTab(t.key)}
                  className={
                    'inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-sm font-semibold border transition-colors whitespace-nowrap ' +
                    (active
                      ? 'bg-brass-soft border-brass text-brass'
                      : 'bg-white border-line text-ink-soft hover:border-brass/50 hover:text-ink')
                  }
                >
                  <Icon name={t.icon} size={14} />
                  <span>{t.label}</span>
                  {tabHasOverride && (
                    <span
                      aria-label="has override"
                      className="inline-block w-1.5 h-1.5 rounded-full bg-brass"
                    />
                  )}
                </button>
              )
            })}
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 scrollbar-clean">
          {tabsToRender.map((t) => (
            <div key={t.key} hidden={tab !== t.key}>
              <TabEditor
                category={t.key}
                scope={scope}
                eventId={targetEventId}
                briefing={briefing}
                plan={plan}
                firstAid={firstAid}
                quickRef={quickRef ?? null}
              />
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 px-6 py-3 border-t border-line flex-shrink-0 bg-white">
          <button
            type="button"
            onClick={onReset}
            disabled={!canReset}
            className={
              'inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-semibold border transition-colors ' +
              (canReset
                ? 'border-line text-ink-soft hover:border-oxblood hover:text-oxblood'
                : 'border-line text-ink-soft cursor-not-allowed opacity-50')
            }
            title={canReset ? 'Clear the override for this tab' : 'No override to reset'}
          >
            <Icon name="x" size={13} />
            Reset
          </button>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-md text-sm font-semibold border border-green text-green bg-green-soft hover:bg-green hover:text-parchment transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

// ---- per-category editors / read-only renders ----------------------------

function TabEditor({
  category,
  scope,
  eventId,
  briefing,
  plan,
  firstAid,
  quickRef
}: {
  category: EventResourceCategory
  scope: Scope
  eventId: number | null
  briefing: SafetyBriefingContent
  plan: EventPlanContent
  firstAid: FirstAidContent
  quickRef: import('../types').QuickReferencePayload | null
}) {
  switch (category) {
    case 'briefing':
      return <SafetyBriefingEditor scope={scope} content={briefing} />
    case 'plan':
      return <EventPlanEditor scope={scope} content={plan} eventId={eventId} />
    case 'first-aid':
      return <FirstAidEditor scope={scope} content={firstAid} />
    case 'activities':
      return <ActivitiesReadOnly eventId={eventId} activities={quickRef?.activities ?? null} />
    case 'maps':
      return <StationMapsReadOnly eventId={eventId} location={quickRef?.location ?? null} />
    case 'contacts':
      return <KeyContactsReadOnly eventId={eventId} contacts={quickRef?.contacts ?? null} />
  }
}

// ---- shared bits ----------------------------------------------------------

function FormLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-xs uppercase font-bold text-ink-soft tracking-wider mb-1">
      {children}
    </div>
  )
}

const TEXTAREA_CLS =
  'w-full font-body text-sm leading-snug border border-line rounded-md p-2 ' +
  'bg-white focus:outline-none focus:border-brass focus:ring-1 focus:ring-brass'

const INPUT_CLS =
  'font-body text-sm leading-snug border border-line rounded-md px-2 py-1.5 ' +
  'bg-white focus:outline-none focus:border-brass focus:ring-1 focus:ring-brass'

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <h4 className="font-display text-base font-semibold mt-1 mb-3 pb-1.5 border-b border-line">
      {children}
    </h4>
  )
}

function PreviewWrapper({ children }: { children: React.ReactNode }) {
  return (
    <div>
      <SectionHeader>Live preview</SectionHeader>
      <div className="bg-white border border-line rounded-lg p-4">
        {children}
      </div>
    </div>
  )
}

// ---- Safety Briefing editor ---------------------------------------------

function SafetyBriefingEditor({
  scope,
  content
}: {
  scope: Scope
  content: SafetyBriefingContent
}) {
  const { setOverride } = useResourceOverrides()

  // Form state seeded from the resolved content. Re-seeds when content
  // changes (e.g. user opens a different scope's editor, or Reset fires).
  const [warning, setWarning] = useState(content.callout.body)
  const [respText, setRespText] = useState(content.responsibilities.join('\n'))
  const [numbers, setNumbers] = useState(
    content.emergencyNumbers.map((n) => ({ name: n.label, phone: n.display }))
  )

  useEffect(() => {
    setWarning(content.callout.body)
    setRespText(content.responsibilities.join('\n'))
    setNumbers(content.emergencyNumbers.map((n) => ({ name: n.label, phone: n.display })))
    // Re-seed when the resolved content changes — e.g. Reset wipes the
    // override and we want the form to snap back to the underlying
    // value (defaults / DB / constants).
  }, [content])

  // Commit on every change. We store all three blocks because the
  // resolver doesn't care about partials at write time — a stored
  // override that matches the constants is harmless and Reset always
  // clears the whole tab. Simpler than per-field dirty-tracking.
  function commit(next: SafetyBriefingOverride) {
    setOverride(scope, 'briefing', next)
  }

  const responsibilities = useMemo(() => splitLines(respText), [respText])

  const previewContent: SafetyBriefingContent = useMemo(
    () => ({
      intro: content.intro,
      callout: { title: content.callout.title, body: warning },
      responsibilities,
      emergencyNumbers: numbers.map((n) => ({
        label: n.name,
        tel: digitsOnly(n.phone),
        display: n.phone
      }))
    }),
    [content.intro, content.callout.title, warning, responsibilities, numbers]
  )

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div>
        <SectionHeader>Fields</SectionHeader>

        <div className="mb-4">
          <FormLabel>Platform-Train Interface warning</FormLabel>
          <textarea
            rows={3}
            value={warning}
            onChange={(e) => {
              setWarning(e.target.value)
              commit({
                warning: e.target.value,
                responsibilities,
                numbers
              })
            }}
            className={TEXTAREA_CLS}
          />
        </div>

        <div className="mb-4">
          <FormLabel>Responsibilities (one per line)</FormLabel>
          <textarea
            rows={5}
            value={respText}
            onChange={(e) => {
              setRespText(e.target.value)
              commit({
                warning,
                responsibilities: splitLines(e.target.value),
                numbers
              })
            }}
            className={TEXTAREA_CLS}
          />
        </div>

        <div className="mb-4">
          <FormLabel>Emergency Numbers</FormLabel>
          <div className="space-y-2">
            {numbers.map((n, i) => (
              <div key={i} className="grid grid-cols-[1fr_auto] gap-2">
                <input
                  value={n.name}
                  onChange={(e) => {
                    const next = numbers.map((row, idx) =>
                      idx === i ? { ...row, name: e.target.value } : row
                    )
                    setNumbers(next)
                    commit({ warning, responsibilities, numbers: next })
                  }}
                  className={INPUT_CLS}
                />
                <input
                  value={n.phone}
                  onChange={(e) => {
                    const next = numbers.map((row, idx) =>
                      idx === i ? { ...row, phone: e.target.value } : row
                    )
                    setNumbers(next)
                    commit({ warning, responsibilities, numbers: next })
                  }}
                  className={`${INPUT_CLS} font-mono`}
                  style={{ minWidth: 140 }}
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      <PreviewWrapper>
        <SafetyBriefingTab content={previewContent} />
      </PreviewWrapper>
    </div>
  )
}

// ---- Event Plan editor --------------------------------------------------

function EventPlanEditor({
  scope,
  content,
  eventId
}: {
  scope: Scope
  content: EventPlanContent
  eventId: number | null
}) {
  const { setOverride } = useResourceOverrides()

  const [operatingPattern, setOperatingPattern] = useState(content.operatingPattern)
  const [todaysProgramme, setTodaysProgramme] = useState(content.todaysProgramme)
  const [visitorFlow, setVisitorFlow] = useState(content.visitorFlow)

  useEffect(() => {
    setOperatingPattern(content.operatingPattern)
    setTodaysProgramme(content.todaysProgramme)
    setVisitorFlow(content.visitorFlow)
  }, [content])

  function commit(next: EventPlanOverride) {
    setOverride(scope, 'plan', next)
  }

  const preview: EventPlanContent = {
    operatingPattern,
    todaysProgramme,
    visitorFlow,
    // The "using fallback" hint only renders when nothing is set anywhere;
    // while editing we always have non-empty values, so suppress the hint.
    usingFallback: false
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div>
        <SectionHeader>Fields</SectionHeader>

        <div className="mb-4">
          <FormLabel>Operating Pattern</FormLabel>
          <textarea
            rows={4}
            value={operatingPattern}
            onChange={(e) => {
              setOperatingPattern(e.target.value)
              commit({
                operatingPattern: e.target.value,
                todaysProgramme,
                visitorFlow
              })
            }}
            className={TEXTAREA_CLS}
          />
        </div>

        <div className="mb-4">
          <FormLabel>Today’s Programme</FormLabel>
          <textarea
            rows={4}
            value={todaysProgramme}
            onChange={(e) => {
              setTodaysProgramme(e.target.value)
              commit({
                operatingPattern,
                todaysProgramme: e.target.value,
                visitorFlow
              })
            }}
            className={TEXTAREA_CLS}
          />
        </div>

        <div className="mb-4">
          <FormLabel>Visitor Flow</FormLabel>
          <textarea
            rows={4}
            value={visitorFlow}
            onChange={(e) => {
              setVisitorFlow(e.target.value)
              commit({
                operatingPattern,
                todaysProgramme,
                visitorFlow: e.target.value
              })
            }}
            className={TEXTAREA_CLS}
          />
        </div>
      </div>

      <PreviewWrapper>
        <EventPlanTab
          eventId={eventId ?? 0}
          operatingPattern={null}
          todaysProgramme={null}
          visitorFlow={null}
          onToast={() => { /* preview pane swallows toasts */ }}
          content={preview}
          disableActions
        />
      </PreviewWrapper>
    </div>
  )
}

// ---- First Aid editor ---------------------------------------------------

function FirstAidEditor({
  scope,
  content
}: {
  scope: Scope
  content: FirstAidContent
}) {
  const { setOverride } = useResourceOverrides()

  const [calloutBody, setCalloutBody] = useState(content.callout.body)
  const [defibLocations, setDefibLocations] = useState(content.defibLocations)
  const [firstAidKits, setFirstAidKits] = useState(content.firstAidKits)

  useEffect(() => {
    setCalloutBody(content.callout.body)
    setDefibLocations(content.defibLocations)
    setFirstAidKits(content.firstAidKits)
  }, [content])

  function commit(next: FirstAidOverride) {
    setOverride(scope, 'first-aid', next)
  }

  const preview: FirstAidContent = {
    callout: { title: content.callout.title, body: calloutBody },
    defibLocations,
    firstAidKits
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div>
        <SectionHeader>Fields</SectionHeader>

        <div className="mb-4">
          <FormLabel>Medical Emergency callout body</FormLabel>
          <textarea
            rows={3}
            value={calloutBody}
            onChange={(e) => {
              setCalloutBody(e.target.value)
              commit({
                emergencyCalloutBody: e.target.value,
                defibLocations,
                firstAidKits
              })
            }}
            className={TEXTAREA_CLS}
          />
        </div>

        <div className="mb-4">
          <FormLabel>Defibrillator Locations</FormLabel>
          <div className="space-y-2">
            {defibLocations.map((d, i) => (
              <div key={i} className="grid grid-cols-[140px_1fr] gap-2">
                <input
                  value={d.location}
                  onChange={(e) => {
                    const next = defibLocations.map((row, idx) =>
                      idx === i ? { ...row, location: e.target.value } : row
                    )
                    setDefibLocations(next)
                    commit({
                      emergencyCalloutBody: calloutBody,
                      defibLocations: next,
                      firstAidKits
                    })
                  }}
                  className={INPUT_CLS}
                />
                <input
                  value={d.where}
                  onChange={(e) => {
                    const next = defibLocations.map((row, idx) =>
                      idx === i ? { ...row, where: e.target.value } : row
                    )
                    setDefibLocations(next)
                    commit({
                      emergencyCalloutBody: calloutBody,
                      defibLocations: next,
                      firstAidKits
                    })
                  }}
                  className={INPUT_CLS}
                />
              </div>
            ))}
          </div>
        </div>

        <div className="mb-4">
          <FormLabel>First Aid Kits paragraph</FormLabel>
          <textarea
            rows={3}
            value={firstAidKits}
            onChange={(e) => {
              setFirstAidKits(e.target.value)
              commit({
                emergencyCalloutBody: calloutBody,
                defibLocations,
                firstAidKits: e.target.value
              })
            }}
            className={TEXTAREA_CLS}
          />
        </div>
      </div>

      <PreviewWrapper>
        <FirstAidTab content={preview} />
      </PreviewWrapper>
    </div>
  )
}

// ---- read-only tabs -----------------------------------------------------

function ReadOnlyNote({
  body,
  linkLabel,
  to
}: {
  body: string
  linkLabel: string
  to: string | null
}) {
  const navigate = useNavigate()
  return (
    <div className="bg-brass-soft border border-brass rounded-lg p-4 mb-4">
      <div className="font-semibold text-brass mb-1">Not editable here</div>
      <div className="text-sm text-ink-soft leading-snug mb-2">{body}</div>
      {to ? (
        <button
          type="button"
          onClick={() => navigate(to)}
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-green hover:underline"
        >
          → {linkLabel}
        </button>
      ) : (
        <div className="text-xs text-ink-soft italic">
          Open the event from /resources to jump to its authoring tab.
        </div>
      )}
    </div>
  )
}

function ActivitiesReadOnly({
  eventId,
  activities
}: {
  eventId: number | null
  activities: import('../types').QuickRefActivity[] | null
}) {
  return (
    <div>
      <ReadOnlyNote
        body="This tab is data-driven from dbo.Activities rows on this event. To change activities, edit the records on the event’s Activities tab."
        linkLabel="Open Activities tab for this event"
        to={eventId != null ? `/events/${eventId}?tab=activities` : null}
      />
      {eventId == null ? (
        <div className="text-xs text-ink-soft italic">
          In defaults mode there is no event-specific activity list to preview.
        </div>
      ) : (
        <ActivitiesTab activities={activities ?? []} />
      )}
    </div>
  )
}

function StationMapsReadOnly({
  eventId,
  location
}: {
  eventId: number | null
  location: string | null
}) {
  return (
    <div>
      <ReadOnlyNote
        body="This tab is driven by the event’s location and the maps in public/station-maps/. To swap the map, edit the event’s location on the Overview tab."
        linkLabel="Open event Overview tab"
        to={eventId != null ? `/events/${eventId}?tab=overview` : null}
      />
      {eventId == null ? (
        <div className="text-xs text-ink-soft italic">
          In defaults mode there is no event-specific map to preview.
        </div>
      ) : (
        <StationMapsTab location={location} />
      )}
    </div>
  )
}

function KeyContactsReadOnly({
  eventId,
  contacts
}: {
  eventId: number | null
  contacts: import('../types').QuickRefContact[] | null
}) {
  return (
    <div>
      <ReadOnlyNote
        body="This tab is driven by the event’s roster assignments. To change who appears, edit the roster on the event’s Roster tab."
        linkLabel="Open Roster tab for this event"
        to={eventId != null ? `/events/${eventId}?tab=roster` : null}
      />
      {eventId == null ? (
        <div className="text-xs text-ink-soft italic">
          In defaults mode there is no event-specific roster to preview.
        </div>
      ) : (
        <KeyContactsTab contacts={contacts ?? []} />
      )}
    </div>
  )
}

// ---- helpers ------------------------------------------------------------

function splitLines(s: string): string[] {
  return s.split('\n').map((l) => l.trim()).filter((l) => l.length > 0)
}

function digitsOnly(raw: string): string {
  return raw.replace(/[^0-9]/g, '')
}
